import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { getContract, getMyContract } from '../../api/contracts';
import Header from '../Header';

import ContractBanner from '../../components/goals/ContractBanner';

import KpaList from '../../components/goal-settings/KpaList';
import KpaModal from '../../components/goals/KpaModal';
import ConfirmModal from '../../components/common/ConfirmModal';
import KpaCascadeModal from '../../components/goals/KpaCascadeModal';
import '../../components/reviews/reviews-table.css'; // if not already imported on this page

import { listStatuses, setContractStatus } from '../../api/contracts';


import KpiList from '../../components/goal-settings/KpiList';
import KpiModal from '../../components/goals/KpiModal';
import KpiCascadeModal from '../../components/goals/KpiCascadeModal';

import CompanyValuesList from '../../components/goals/CompanyValuesList';
import CompanyValueModal from '../../components/goals/CompanyValueModal';

import ContractApprovalsPanel from '../../components/goals/ContractApprovalsPanel';

import api from '../../api/axios';

import { getActiveCycle } from '../../api/cycles';

import { getOrCreateContract, validateKpaWeights,
    approveContractAsUser, markContractComplete, approveContractAsManager } from '../../api/contracts';

import { listTimeframes } from '../../api/timeframes';

import { listKpas, createKpa, updateKpa, deleteKpa,
    fetchManagerKpas, cascadeManagerKpas } from '../../api/kpas';

import { listKpis, createKpi, updateKpi, deleteKpi, validateKpiWeights,
    fetchManagerKpisForKpa, cascadeManagerKpis } from '../../api/kpis';

import { listCompanyValues, createCompanyValue, updateCompanyValue, deleteCompanyValue } from '../../api/companyValues';

const GoalSettings = () => {

    const [statuses, setStatuses] = useState([]);

    useEffect(() => {
        (async () => {
            try { setStatuses(await listStatuses()); } catch {}
        })();
    }, []);

    const { contractId } = useParams();
    const [canEdit, setCanEdit] = useState(false);
    const [canApprove, setCanApprove] = useState(false);

    // Auth + contract
    const [user, setUser] = useState(null);
    const [contract, setContract] = useState(null);
    const [cycle, setCycle] = useState(null);
    const [toast, setToast] = useState(null);

    // Active cycle (for Company Values)
    const [activeCycleId, setActiveCycleId] = useState(null);
    const [activeCycle, setActiveCycle] = useState(null);
    const [cycleError, setCycleError] = useState(null);

    // Timeframes
    const [timeframes, setTimeframes] = useState([]);

    // KPAs
    const [kpas, setKpas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [editingKpa, setEditingKpa] = useState(null);
    const [confirmDelete, setConfirmDelete] = useState({ open: false, kpa: null, busy: false });
    const [kpaTotals, setKpaTotals] = useState({ kpaTotal: 0 });

    // KPIs
    const [selectedKpa, setSelectedKpa] = useState(null);
    const [kpis, setKpis] = useState([]);
    const [kpiToast, setKpiToast] = useState(null);
    const [showKpiCreate, setShowKpiCreate] = useState(false);
    const [showKpiEdit, setShowKpiEdit] = useState(false);
    const [editingKpi, setEditingKpi] = useState(null);
    const [validatingKpi, setValidatingKpi] = useState(false);

    // Cascades
    const [showCascade, setShowCascade] = useState(false);
    const [showKpiCascade, setShowKpiCascade] = useState(false);

    // Company Values
    const [cvItems, setCvItems] = useState([]);
    const [cvLoading, setCvLoading] = useState(false);
    const [cvToast, setCvToast] = useState(null);
    const [showCvCreate, setShowCvCreate] = useState(false);
    const [showCvEdit, setShowCvEdit] = useState(false);
    const [editingCv, setEditingCv] = useState(null);
    const [validatingCv, setValidatingCv] = useState(false);

    // Approvals
    const [approvalsBusy, setApprovalsBusy] = useState(false);

    // ===== Derived totals =====
    const kpaTotal = useMemo(() => (kpas || []).reduce((s, k) => s + Number(k.weight || 0), 0), [kpas]);
    const kpaRemaining = useMemo(() => Math.max(0, 90 - kpaTotal), [kpaTotal]);

    const kpiTotal = useMemo(() => (kpis || []).reduce((s, x) => s + Number(x.weight || 0), 0), [kpis]);
    const kpiRemaining = useMemo(() => Math.max(0, 100 - kpiTotal), [kpiTotal]);

    const cvTotal  = useMemo(() => (cvItems || []).reduce((s, x) => s + Number(x.weight || 0), 0), [cvItems]);
    const cvRemaining = useMemo(() => Math.max(0, 10 - cvTotal), [cvTotal]);

    const totals = useMemo(() => ({
        kpa_total: Number(kpaTotal.toFixed(2)),
        cv_total: Number(cvTotal.toFixed(2)),
        ok: Number(kpaTotal.toFixed(2)) === 90.00 && Number(cvTotal.toFixed(2)) === 10.00,
    }), [kpaTotal, cvTotal]);

    // ===== Loaders =====
    // 1) Current user
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get('/api/user');
                if (!alive) return;
                setUser(data);
            } catch {/* no-op */}
        })();
        return () => { alive = false; };
    }, []);

    // 2) Active cycle (for CVs)
    useEffect(() => {
        (async () => {
            try {
                const c = await getActiveCycle();
                setActiveCycle(c);
                setActiveCycleId(c?.id ?? null);
            } catch (e) {
                setCycleError(e?.response?.data?.message || 'No active cycle found.');
                setActiveCycleId(null);
            }
        })();
    }, []);

    // 3) Timeframes
    useEffect(() => {
        (async () => {
            try {
                setTimeframes(await listTimeframes());
            } catch {/* no-op */}
        })();
    }, []);

    // 4) Contract (create if missing)
    const refreshContract = async () => {
        const c = await getOrCreateContract(); // no args
        setContract(c);
        setCycle(c.cycle ?? (c?.cycle_id ? { id: c.cycle_id } : null));
    };


    useEffect(() => {
        if (!user?.id) return;
        refreshContract().catch(() => setToast({ type: 'danger', text: 'Failed to load/create contract.' }));
    }, [user?.id]);

    // 5) KPAs for the contract
    const refreshKpas = async () => {
        if (!contract?.id) return;
        try {
            const items = await listKpas(contract.id);
            setKpas(items);
        } catch {
            setToast({ type: 'danger', text: 'Failed to load KPAs.' });
        }
    };
    useEffect(() => { if (contract?.id) refreshKpas(); }, [contract?.id]);

    // 6) Company values for active cycle
    const refreshCompanyValues = async () => {
        if (!activeCycleId) return;
        const list = await listCompanyValues(activeCycleId);
        setCvItems(list);
    };
    useEffect(() => {
        if (!activeCycleId) return;
        (async () => {
            setCvLoading(true);
            try {
                await refreshCompanyValues();
            } finally {
                setCvLoading(false);
            }
        })();
    }, [activeCycleId]);

    // ===== KPA handlers =====
    const handleValidateKpas = async () => {
        if (!contract?.id) return;
        setLoading(true);
        try {
            const res = await validateKpaWeights(contract.id);
            setToast({ type: 'success', text: res?.message ?? 'KPA weights OK.' });
        } catch (e) {
            const msg = e?.response?.data?.message || 'Validation failed.';
            const total = e?.response?.data?.details?.total;
            setToast({ type: 'warning', text: total != null ? `${msg} (Total = ${total})` : msg });
        } finally {
            setLoading(false);
            refreshKpas();
        }
    };

    const handleCreateKpa = async (dto) => {
        await createKpa({ ...dto, contract_id: contract.id });
        await refreshKpas();
        setToast({ type: 'success', text: 'KPA created.' });
    };

    const openEditKpa = (kpa) => { setEditingKpa(kpa); setShowEdit(true); };

    const handleEditKpa = async (patch) => {
        await updateKpa(editingKpa.id, patch);
        setShowEdit(false);
        setEditingKpa(null);
        await refreshKpas();
        setToast({ type: 'success', text: 'KPA updated.' });
    };

    const requestDeleteKpa = (kpa) => setConfirmDelete({ open: true, kpa, busy: false });

    const confirmDeleteNow = async () => {
        setConfirmDelete(s => ({ ...s, busy: true }));
        try {
            await deleteKpa(confirmDelete.kpa.id);
            setConfirmDelete({ open: false, kpa: null, busy: false });
            await refreshKpas();
            setToast({ type: 'success', text: 'KPA deleted.' });
        } catch {
            setConfirmDelete(s => ({ ...s, busy: false }));
            setToast({ type: 'danger', text: 'Failed to delete KPA.' });
        }
    };

    // Cascade KPA
    const openCascade = () => setShowCascade(true);
    const loadManagerKpas = async () => fetchManagerKpas(contract.id);
    const submitCascade = async (payload) => {
        const res = await cascadeManagerKpas(contract.id, payload);
        setToast({ type: 'success', text: res?.message || 'Cascaded.' });
        await refreshKpas();
    };

    // ===== KPI handlers =====
    const refreshKpis = async () => {
        if (!selectedKpa?.id) return;
        const items = await listKpis(selectedKpa.id);
        setKpis(items);
    };

    const handleManageKpis = async (kpa) => {
        setSelectedKpa(kpa);
        await refreshKpis();
    };

    const handleCreateKpi = async (dto) => {
        await createKpi({ ...dto, key_performance_area_id: selectedKpa.id });
        await refreshKpis();
        setKpiToast({ type: 'success', text: 'KPI created.' });
    };

    const openEditKpi = (kpi) => { setEditingKpi(kpi); setShowKpiEdit(true); };

    const handleEditKpi = async (patch) => {
        await updateKpi(editingKpi.id, patch);
        setShowKpiEdit(false);
        setEditingKpi(null);
        await refreshKpis();
        setKpiToast({ type: 'success', text: 'KPI updated.' });
    };

    const handleDeleteKpi = async (kpi) => {
        if (!window.confirm(`Delete KPI "${kpi.name}"?`)) return;
        await deleteKpi(kpi.id);
        await refreshKpis();
        setKpiToast({ type: 'success', text: 'KPI deleted.' });
    };

    const handleValidateKpis = async () => {
        if (!selectedKpa?.id) return;
        setValidatingKpi(true);
        try {
            const res = await validateKpiWeights(selectedKpa.id);
            setKpiToast({ type: 'success', text: res?.message ?? 'KPI weights OK.' });
        } catch (e) {
            const msg = e?.response?.data?.message || 'Validation failed.';
            const total = e?.response?.data?.details?.total;
            setKpiToast({ type: 'warning', text: total != null ? `${msg} (Total = ${total})` : msg });
        } finally {
            setValidatingKpi(false);
            refreshKpis();
        }
    };

    // Cascade KPI
    const openKpiCascade = () => setShowKpiCascade(true);
    const loadManagerKpis = async () => fetchManagerKpisForKpa(selectedKpa.id);
    const submitKpiCascade = async (payload) => {
        const res = await cascadeManagerKpis(selectedKpa.id, payload);
        setKpiToast({ type: 'success', text: res?.message || 'KPIs cascaded.' });
        await refreshKpis();
    };

    // ===== Company Values handlers =====
    const openCvCreate = () => setShowCvCreate(true);
    const openCvEdit = (v) => { setEditingCv(v); setShowCvEdit(true); };

    const submitCreateCv = async (dto) => {
        await createCompanyValue(dto);
        await refreshCompanyValues();
        setCvToast({ type: 'success', text: 'Company value added.' });
    };

    const submitEditCv = async (patch) => {
        await updateCompanyValue(editingCv.id, patch);
        setShowCvEdit(false);
        setEditingCv(null);
        await refreshCompanyValues();
        setCvToast({ type: 'success', text: 'Company value updated.' });
    };

    const removeCv = async (v) => {
        if (!window.confirm(`Delete "${v.name}"?`)) return;
        await deleteCompanyValue(v.id);
        await refreshCompanyValues();
        setCvToast({ type: 'success', text: 'Company value deleted.' });
    };

    const validateCv = async () => {
        setValidatingCv(true);
        try {
            const total = cvItems.reduce((s, x) => s + Number(x.weight || 0), 0);
            if (Math.abs(total - 10) < 0.001) {
                setCvToast({ type: 'success', text: 'Total weight = 10.00 ✓' });
            } else {
                setCvToast({ type: 'warning', text: `Total is ${total.toFixed(2)}. It must equal 10.00.` });
            }
        } finally {
            setValidatingCv(false);
        }
    };

    // ===== Approvals handlers =====
    const doApproveUser = async () => {
        if (!contract?.id) return;
        const kpaReady = Number(totals?.kpa_total ?? 0) === 90;
        const isOwner = user?.id && contract?.user_id && user.id === contract.user_id;
        if (!isOwner) {
            setToast({ type: 'warning', text: 'Only the contract owner can approve as employee.' });
            return;
        }
        if (!kpaReady) {
            setToast({ type: 'warning', text: 'KPA total must equal 90 before approval.' });
            return;
        }
        setApprovalsBusy(true);
        try {
            const res = await approveContractAsUser(contract.id);
            setToast({ type: 'success', text: res?.message || 'Approved.' });
            await refreshContract();
        } catch (e) {
            setToast({ type: 'danger', text: e?.response?.data?.message || 'Failed.' });
        } finally {
            setApprovalsBusy(false);
        }
    };

    const doMarkComplete = async () => {
        if (!contract?.id) return;
        setApprovalsBusy(true);
        try {
            const res = await markContractComplete(contract.id);
            setToast({ type: 'success', text: res?.message || 'Marked complete.' });
            await refreshContract();
        } catch (e) {
            setToast({ type: 'danger', text: e?.response?.data?.message || 'Failed.' });
        } finally {
            setApprovalsBusy(false);
        }
    };

    const doApproveManager = async () => {
        if (!contract?.id) return;
        setApprovalsBusy(true);
        try {
            const res = await approveContractAsManager(contract.id);
            setToast({ type: 'success', text: res?.message || 'Manager approved.' });
            await refreshContract();
        } catch (e) {
            setToast({ type: 'danger', text: e?.response?.data?.message || 'Failed.' });
        } finally {
            setApprovalsBusy(false);
        }
    };

    const loadContractContext = async () => {
        if (contractId) {
            // viewing a specific contract (could be employee)
            const c = await getContract(contractId);
            setContract(c);
            setCycle(c.cycle ?? (c?.cycle_id ? { id: c.cycle_id } : null));

            // capabilities
            setCanEdit(c.user_id === user?.id);                  // owner only
            setCanApprove(user?.id === c?.user?.manager_id);     // manager only
        } else {
            // /contracts/mine
            const mine = await getMyContract();
            setContract(mine);
            setCycle(mine.cycle ?? (mine?.cycle_id ? { id: mine.cycle_id } : null));
            setCanEdit(true);
            setCanApprove(user?.id === mine?.user?.manager_id);
        }
    };

    useEffect(() => { if (user) loadContractContext(); }, [user, contractId]);


    const onSetStatus = async (statusId, selectedStatus = null) => {
        if (!contract?.id) return;
        const kpaReady = Number(totals?.kpa_total ?? 0) === 90;
        const isManager = user?.id && contract?.user?.manager_id && user.id === contract.user.manager_id;
        if (!isManager) {
            setToast({ type: 'warning', text: 'Only the manager can update the contract status.' });
            return;
        }
        if (!kpaReady) {
            setToast({ type: 'warning', text: 'KPA total must equal 90 before changing status.' });
            return;
        }

        const status = selectedStatus ?? statuses.find(s => Number(s.id) === Number(statusId));
        const statusName = (status?.name || '').toLowerCase();
        const isApprovedStatus = statusName === 'approved';
        const isReferBack = statusName === 'refer back';

        setApprovalsBusy(true);
        try {
            await setContractStatus(contract.id, statusId);
            if (isApprovedStatus) {
                try {
                    const res = await approveContractAsManager(contract.id);
                    setToast({ type: 'success', text: res?.message || 'Manager approval recorded.' });
                } catch (err) {
                    setToast({ type: 'warning', text: err?.response?.data?.message || 'Status updated but manager approval failed.' });
                }
            } else if (isReferBack) {
                setToast({ type: 'success', text: 'Status updated. Employee approval was cleared.' });
            } else {
                setToast({ type: 'success', text: 'Contract status updated.' });
            }
            await refreshContract(); // so the banner/panel reflect new status
        } catch (e) {
            setToast({ type: 'danger', text: e?.response?.data?.message || 'Failed to update status.' });
        } finally {
            setApprovalsBusy(false);
        }
    };

    return (
        <>
            <Header label={<p className="py-0 my-0">Goal Settings</p>} />
            <main id="main" className="main">
                <div className="row">
                    <div className="col-lg-12">
                        {cycleError && (
                            <div className="alert alert-warning my-2">{cycleError}</div>
                        )}

                        {/*<ContractBanner
                            contract={contract}
                            cycle={contract?.cycle || cycle}
                            kpaTotal={kpaTotal}
                            onValidate={handleValidateKpas}
                            loading={loading}
                        />*/}



                        <KpaList
                            contractId={contract?.id}
                            kpaWeighting={contract?.key_performance_area_weighting ?? 90}
                            onTotalsChange={setKpaTotals}
                        />


                        {selectedKpa && (
                            <>
                                <KpiList
                                    kpa={selectedKpa}
                                    kpis={kpis}
                                    total={kpiTotal}
                                    onAdd={() => setShowKpiCreate(true)}
                                    onEdit={openEditKpi}
                                    onDelete={handleDeleteKpi}
                                    onValidate={handleValidateKpis}
                                    validating={validatingKpi}
                                    onCascade={openKpiCascade}
                                />

                                {kpiToast && (
                                    <div className={`alert alert-${kpiToast.type} mt-3`} role="alert">
                                        {kpiToast.text}
                                    </div>
                                )}

                                {/* Create KPI */}
                                <KpiModal
                                    show={showKpiCreate}
                                    mode="create"
                                    onClose={() => setShowKpiCreate(false)}
                                    onSubmit={handleCreateKpi}
                                    remaining={kpiRemaining}
                                />

                                {/* Edit KPI */}
                                <KpiModal
                                    show={showKpiEdit}
                                    mode="edit"
                                    initial={editingKpi}
                                    onClose={() => { setShowKpiEdit(false); setEditingKpi(null); }}
                                    onSubmit={handleEditKpi}
                                    remaining={kpiRemaining}
                                />

                                {/* Cascade KPI */}
                                <KpiCascadeModal
                                    show={showKpiCascade}
                                    onClose={() => setShowKpiCascade(false)}
                                    kpa={selectedKpa}
                                    remaining={kpiRemaining}
                                    loadManagerKpis={loadManagerKpis}
                                    onSubmitCascade={submitKpiCascade}
                                />
                            </>
                        )}

                        {cvToast && (
                            <div className={`alert alert-${cvToast.type} mt-3`} role="alert">
                                {cvToast.text}
                            </div>
                        )}

                        {/* Company Values */}
                        <CompanyValuesList
                            items={cvItems}
                            total={cvTotal}
                            readOnly={true}
                        />

                        <CompanyValueModal
                            show={showCvCreate}
                            mode="create"
                            cycleId={activeCycleId}
                            remaining={cvRemaining}
                            onClose={() => setShowCvCreate(false)}
                            onSubmit={submitCreateCv}
                        />

                        <CompanyValueModal
                            show={showCvEdit}
                            mode="edit"
                            cycleId={activeCycleId}
                            initial={editingCv}
                            remaining={cvRemaining}
                            onClose={() => { setShowCvEdit(false); setEditingCv(null); }}
                            onSubmit={submitEditCv}
                        />
                        {toast && (
                            <div className={`alert alert-${toast.type} mt-3`} role="alert">
                                {toast.text}
                            </div>
                        )}
                        {/* Approvals */}
                        <ContractApprovalsPanel
                            contract={contract}
                            currentUser={user}
                            totals={totals}
                            busy={approvalsBusy}
                            onApproveUser={doApproveUser}
                            statuses={statuses}
                            onSetStatus={onSetStatus}
                        />

                    </div>
                </div>
            </main>

            {/* Create KPA */}
            <KpaModal
                show={showCreate}
                mode="create"
                onClose={() => setShowCreate(false)}
                onSubmit={handleCreateKpa}
                timeframes={timeframes}
                remaining={kpaRemaining}
            />

            {/* Edit KPA */}
            <KpaModal
                show={showEdit}
                mode="edit"
                initial={editingKpa}
                onClose={() => { setShowEdit(false); setEditingKpa(null); }}
                onSubmit={handleEditKpa}
                timeframes={timeframes}
                remaining={kpaRemaining}
            />

            {/* Confirm Delete KPA */}
            <ConfirmModal
                show={confirmDelete.open}
                title="Delete KPA"
                message={`Are you sure you want to delete “${confirmDelete.kpa?.name ?? ''}”? This will detach any child KPAs.`}
                confirmText="Delete"
                onConfirm={confirmDeleteNow}
                onClose={() => setConfirmDelete({ open: false, kpa: null, busy: false })}
                busy={confirmDelete.busy}
            />

            {/* Cascade KPA */}
            <KpaCascadeModal
                show={showCascade}
                onClose={() => setShowCascade(false)}
                loadManagerKpas={loadManagerKpas}
                remaining={kpaRemaining}
                onSubmitCascade={submitCascade}
            />
        </>
    );
};

export default GoalSettings;
